import {currencies, transactionTypes, videoVerificationStatuses, withdrawalHoldReasons} from '@bdswiss/common-enums'

import {getFormattedAmount} from '../common/utils/general'
import {getAccountLabel, isCentAccount} from '../common/utils/accounts'

export const VIDEO_VERIFICATION_HOLD_CODE = withdrawalHoldReasons.videoVerification.value

/** The screens of the pop-up. The letters are the ones of the spec (V3, section 3). */
export type VideoVerificationScreen =
  | 'required' // A
  | 'sumsub' // B
  | 'checking' // C
  | 'retry' // D
  | 'locked' // E
  | 'rejected' // F
  | 'down' // G

export type VideoVerificationStatus = keyof typeof videoVerificationStatuses

export type HeldWithdrawal = {
  id: number
  amount: number
  currency: string
  paymentVendor?: string | null
  account?: Record<string, any> | null
}

export type HeldWithdrawalSummary = {
  amount: string
  method: string
  accountName: string
  login: string
}

export const isVideoVerificationHold = (withdrawal?: {holdCode?: string | null} | null): boolean =>
  withdrawal?.holdCode === VIDEO_VERIFICATION_HOLD_CODE

/** The screen to open for the status that the server holds for the client. */
export const screenForStatus = (status?: string | null): VideoVerificationScreen => {
  switch (status) {
    case videoVerificationStatuses.inReview.key:
      return 'checking'
    case videoVerificationStatuses.retry.key:
      return 'retry'
    case videoVerificationStatuses.rejected.key:
      return 'rejected'
    default:
      return 'required'
  }
}

type SdkReviewResult = {reviewAnswer?: string; reviewRejectType?: string}

/**
 * The screen that follows a message of the Sumsub WebSDK, or null when the message changes nothing.
 * Sumsub counts the failed tries, so the pop-up shows screen E only when Sumsub says it has no tries left.
 */
export const screenForSdkMessage = (
  type: string,
  payload: {reviewResult?: SdkReviewResult} | undefined,
): VideoVerificationScreen | null => {
  if (type === 'idCheck.onApplicantSubmitted' || type === 'idCheck.onApplicantResubmitted') return 'checking'
  if (type !== 'idCheck.onApplicantStatusChanged' && type !== 'idCheck.applicantStatus') return null

  const result = payload?.reviewResult
  switch (result?.reviewAnswer) {
    case 'GREEN':
      return 'checking'
    case 'RED':
      return result.reviewRejectType === 'FINAL' ? 'rejected' : 'retry'
    default:
      return null
  }
}

const TRIES_LIMIT_PATTERN = /attempt|too many|limit|try again (tomorrow|later)/i

/** Sumsub reports an error with a code and a text. A message about tries means screen E, any other error means G. */
export const screenForSdkError = (error?: {code?: string; error?: string; reason?: string} | null): VideoVerificationScreen => {
  const text = [error?.code, error?.error, error?.reason].filter(Boolean).join(' ')
  return TRIES_LIMIT_PATTERN.test(text) ? 'locked' : 'down'
}

const displayAmount = (withdrawal: HeldWithdrawal): {amount: number; currency: string} => {
  if (isCentAccount(withdrawal.account as any)) {
    return {
      amount: withdrawal.amount / (currencies.CUD.baseCurrencyRate ?? 1),
      currency: currencies.CUD.baseCurrency ?? withdrawal.currency,
    }
  }
  return {amount: withdrawal.amount, currency: withdrawal.currency}
}

const unique = <T>(values: T[]): T[] => Array.from(new Set(values))

/** The text of an amount that sums the held withdrawals, one sum per currency. */
export const formatHeldAmount = (withdrawals: HeldWithdrawal[]): string => {
  const sums = new Map<string, number>()
  withdrawals.forEach((withdrawal) => {
    const {amount, currency} = displayAmount(withdrawal)
    sums.set(currency, (sums.get(currency) ?? 0) + amount)
  })
  return Array.from(sums.entries())
    .map(([currency, amount]) => getFormattedAmount({amount, currency}))
    .join(' + ')
}

/** The values for the summary box and the banner. All held withdrawals count as one request. */
export const summarizeHeldWithdrawals = (
  withdrawals: HeldWithdrawal[],
  locale: string,
  methodLabel: (vendor: string) => string,
): HeldWithdrawalSummary => {
  // The summary box names one account, so it adds up the withdrawals of that account only.
  const first = withdrawals.find((withdrawal) => withdrawal.account)?.account as Record<string, any> | undefined
  const ofFirstAccount = first ? withdrawals.filter((withdrawal) => withdrawal.account?.id === first.id) : withdrawals
  return {
    amount: formatHeldAmount(ofFirstAccount),
    method: unique(
      ofFirstAccount.map((withdrawal) => (withdrawal.paymentVendor ? methodLabel(withdrawal.paymentVendor) : '')).filter(Boolean),
    ).join(', '),
    accountName: first ? getAccountLabel(first as any, locale) : '',
    login: first?.remoteId != null ? String(first.remoteId) : '',
  }
}

export type HistoryVerificationState = 'waiting' | 'review' | 'hold'

/**
 * The chip of a History row that is held for the video verification, or null for any other row.
 * The row waits while the client can still do the check, and is on hold after the final rejection.
 */
export const historyStateOf = (
  row: {id: number; transactionType?: string},
  heldIds: ReadonlySet<number>,
  status?: string | null,
): HistoryVerificationState | null => {
  if (row.transactionType !== transactionTypes.withdrawal.key || !heldIds.has(row.id)) return null
  if (status === videoVerificationStatuses.inReview.key) return 'review'
  if (status === videoVerificationStatuses.rejected.key) return 'hold'
  return 'waiting'
}

/** The row link "Continue video verification" shows only while the client can do something. */
export const canContinueVerification = (state: HistoryVerificationState | null): boolean => state === 'waiting'
